import React, { useEffect, useRef, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in Leaflet with React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const LocationMap = ({ latitude, longitude, altitude, timestamp }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const isMapInitialized = useRef(false);

  // Cleanup function
  const cleanupMap = useCallback(() => {
    if (markerRef.current && mapInstanceRef.current) {
      try {
        mapInstanceRef.current.removeLayer(markerRef.current);
      } catch (e) {
        // Ignore cleanup errors
      }
      markerRef.current = null;
    }
    
    if (mapInstanceRef.current) {
      try {
        mapInstanceRef.current.remove();
      } catch (e) {
        // Ignore cleanup errors
      }
      mapInstanceRef.current = null;
      isMapInitialized.current = false;
    }
  }, []);

  // Initialize map only once
  useEffect(() => {
    if (!mapRef.current || isMapInitialized.current) return;

    try {
      // Initialize map with default coordinates
      const initialLat = latitude || 41.0082; // Istanbul default
      const initialLng = longitude || 28.9784;
      
      mapInstanceRef.current = L.map(mapRef.current, {
        zoomControl: true,
        scrollWheelZoom: true,
        doubleClickZoom: true,
        boxZoom: true,
        keyboard: true,
        dragging: true,
        attributionControl: true
      }).setView([initialLat, initialLng], 13);

      // Add tile layer
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 18
      }).addTo(mapInstanceRef.current);

      isMapInitialized.current = true;

      // Force a resize to ensure proper rendering
      setTimeout(() => {
        if (mapInstanceRef.current) {
          mapInstanceRef.current.invalidateSize();
        }
      }, 100);

    } catch (error) {
      console.error('Map initialization error:', error);
    }

    return () => {
      cleanupMap();
    };
  }, []); // Only run once on mount

  // Update marker when coordinates change
  useEffect(() => {
    if (!mapInstanceRef.current || !isMapInitialized.current) return;
    if (!latitude || !longitude) return;

    const validLat = parseFloat(latitude);
    const validLng = parseFloat(longitude);

    if (isNaN(validLat) || isNaN(validLng)) return;

    try {
      // Remove previous marker safely
      if (markerRef.current) {
        try {
          mapInstanceRef.current.removeLayer(markerRef.current);
        } catch (e) {
          // Ignore removal errors
        }
        markerRef.current = null;
      }

      // Update map view with animation
      mapInstanceRef.current.setView([validLat, validLng], 13, {
        animate: true,
        duration: 1
      });

      // Add new marker
      const popupContent = `
        <div>
          <strong>Mevcut Konum</strong><br/>
          <strong>Enlem:</strong> ${validLat.toFixed(6)}<br/>
          <strong>Boylam:</strong> ${validLng.toFixed(6)}<br/>
          ${altitude ? `<strong>Yükseklik:</strong> ${parseFloat(altitude).toFixed(1)}m<br/>` : ''}
          ${timestamp ? `<strong>Zaman:</strong> ${new Date(timestamp * 1000).toLocaleString('tr-TR')}<br/>` : ''}
        </div>
      `;

      markerRef.current = L.marker([validLat, validLng])
        .addTo(mapInstanceRef.current)
        .bindPopup(popupContent, {
          offset: [0, -20],
          closeButton: true,
          autoClose: false
        })
        .openPopup();

    } catch (error) {
      console.error('Marker update error:', error);
    }
  }, [latitude, longitude, altitude, timestamp]);

  // Window resize handler to fix map rendering issues
  useEffect(() => {
    const handleResize = () => {
      if (mapInstanceRef.current && isMapInitialized.current) {
        setTimeout(() => {
          mapInstanceRef.current.invalidateSize();
        }, 200);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    if (typeof timestamp === 'string') {
      const d = new Date(timestamp);
      return isNaN(d) ? 'N/A' : d.toLocaleString('tr-TR');
    }
    if (typeof timestamp === 'number') {
      const ms = timestamp < 1e12 ? timestamp * 1000 : timestamp;
      const d = new Date(ms);
      return isNaN(d) ? 'N/A' : d.toLocaleString('tr-TR');
    }
    return 'N/A';
  };

  return (
    <div className="space-y-4">
      {/* Koordinat Bilgileri */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-blue-50 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-2">Enlem</h4>
          <p className="text-2xl font-bold text-blue-600">
            {latitude ? parseFloat(latitude).toFixed(6) : 'N/A'}°
          </p>
        </div>
        
        <div className="bg-green-50 rounded-lg p-4">
          <h4 className="font-medium text-green-900 mb-2">Boylam</h4>
          <p className="text-2xl font-bold text-green-600">
            {longitude ? parseFloat(longitude).toFixed(6) : 'N/A'}°
          </p>
        </div>
        
        <div className="bg-purple-50 rounded-lg p-4">
          <h4 className="font-medium text-purple-900 mb-2">Yükseklik</h4>
          <p className="text-2xl font-bold text-purple-600">
            {altitude ? `${parseFloat(altitude).toFixed(1)}m` : 'N/A'}
          </p>
        </div>
        
        <div className="bg-orange-50 rounded-lg p-4">
          <h4 className="font-medium text-orange-900 mb-2">Son Güncelleme</h4>
          <p className="text-sm font-medium text-orange-600">
            {formatTimestamp(timestamp)}
          </p>
        </div>
      </div>

      {/* Harita */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h4 className="font-medium text-gray-900">Mevcut Konum</h4>
          <p className="text-sm text-gray-600">Gateway'in son bilinen konumu</p>
        </div>
        <div 
          ref={mapRef} 
          className="w-full"
          style={{ height: '400px', minHeight: '400px' }}
        />
      </div>
    </div>
  );
};

export default LocationMap;
